import React, { useState, useEffect } from 'react';
import { useNotificationContext } from '../context/NotificationContext';
import '../styles/OTPVerification.css';

interface OTPVerificationProps {
  isOpen: boolean;
  onClose: () => void;
  phone: string;
  onVerificationSuccess: (data?: any) => void;
  onResendOTP: () => void;
  type: 'signup' | 'login' | 'booking';
  bookingId?: number | null;
}

const OTPVerification: React.FC<OTPVerificationProps> = ({ 
  isOpen, 
  onClose, 
  phone, 
  onVerificationSuccess,
  onResendOTP,
  type = 'signup',
  bookingId = null
}) => {
  const { showError, showWarning } = useNotificationContext();
  const [otp, setOtp] = useState(['', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [timer, setTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);

  // Timer countdown for resend OTP
  useEffect(() => {
    if (isOpen && timer > 0) {
      const interval = setInterval(() => {
        setTimer(prev => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [isOpen, timer]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setOtp(['', '', '', '']);
      setTimer(60);
      setCanResend(false);
      setIsLoading(false);
    }
  }, [isOpen]);

  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) return; // Only allow single digit
    if (!/^\d*$/.test(value)) return; // Only allow numbers

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 3) {
      const nextInput = document.getElementById(`otp-input-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    // Handle backspace
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-input-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const otpCode = otp.join('');
    
    if (otpCode.length !== 4) {
      showWarning('Please enter complete 4-digit OTP');
      return;
    }

    setIsLoading(true);

    try {
      // Call appropriate OTP verification API based on type
      let endpoint, requestBody;
      
      if (type === 'login') {
        endpoint = 'http://localhost:5000/api/v1/auth/verify-otp-login';
        requestBody = { phone: phone, otp: otpCode };
      } else if (type === 'booking') {
        endpoint = 'http://localhost:5000/api/v1/bookings/verify-otp';
        requestBody = { phone: phone, otp: otpCode, bookingId: bookingId };
      } else {
        endpoint = 'http://localhost:5000/api/v1/auth/verify-otp';
        requestBody = { phone: phone, otp: otpCode, type: 'signup' };
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const result = await response.json();

      if (result.success) {
        onVerificationSuccess(result.data);
        onClose();
      } else {
        showError(result.message || 'Invalid OTP. Please try again.');
        setOtp(['', '', '', '']);
        // Focus first input
        const firstInput = document.getElementById('otp-input-0');
        if (firstInput) firstInput.focus();
      }
    } catch (error) {
      console.error('OTP verification error:', error);
      showError('Failed to verify OTP. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (!canResend) return;

    try {
      await onResendOTP();
      setTimer(60);
      setCanResend(false);
      setOtp(['', '', '', '']);
      
      // Focus first input
      const firstInput = document.getElementById('otp-input-0');
      if (firstInput) firstInput.focus();
    } catch (error) {
      console.error('Resend OTP error:', error);
      showError('Failed to resend OTP. Please try again.');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content otp-modal">
        <button className="close-btn" onClick={onClose}>×</button>
        
        <div className="otp-header">
          <div className="otp-icon">
            <i className="fas fa-mobile-alt"></i>
          </div>
          <h2>
            {type === 'login' ? 'Login Verification' : 
             type === 'booking' ? 'Booking Verification' : 
             'Verify Your Mobile Number'}
          </h2>
          <p className="otp-description">
            We've sent a 4-digit OTP to <strong>{phone}</strong>
            {type === 'login' && ' for login verification'}
            {type === 'booking' && ' to confirm your booking'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="otp-form">
          <div className="otp-inputs">
            {otp.map((digit, index) => (
              <input
                key={index}
                id={`otp-input-${index}`}
                type="text"
                value={digit}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                className="otp-input"
                maxLength={1}
                autoComplete="off"
              />
            ))}
          </div>

          <button 
            type="submit" 
            className={`btn btn-primary otp-submit-btn ${isLoading ? 'loading' : ''}`}
            disabled={isLoading || otp.join('').length !== 4}
          >
            {isLoading ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                Verifying...
              </>
            ) : (
              type === 'login' ? 'Verify & Login' : 
              type === 'booking' ? 'Verify & Confirm Booking' : 
              'Verify & Register'
            )}
          </button>

          <div className="otp-actions">
            <div className="timer-section">
              {!canResend ? (
                <span className="timer-text">
                  Resend OTP in <strong>{timer}s</strong>
                </span>
              ) : (
                <button 
                  type="button" 
                  onClick={handleResendOTP}
                  className="resend-btn"
                >
                  <i className="fas fa-redo"></i>
                  Resend OTP
                </button>
              )}
            </div>
            
            <p className="help-text">
              Didn't receive the code? Check your SMS or try resending.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OTPVerification;